import React, { useState } from 'react';
import { Plus, Minus, Check } from 'lucide-react';
import { fmtIndex, fmtInt, fmtPct, indexTone } from './format';

const COLLAPSED_ROWS = 8;

function Bar({ pct, className }) {
  const w = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="h-2 flex-1 rounded bg-gray-100 overflow-hidden">
      <div className={`h-full rounded ${className}`} style={{ width: `${w}%` }} />
    </div>
  );
}

function ValueRow({ v, onAddRule, isRuleActive, baselineLabel }) {
  const active = isRuleActive?.(v);
  const tooltip = [
    `${v.value_label}`,
    `converters ${fmtPct(v.share_conv, 1)} · ${baselineLabel} ${fmtPct(v.share_base, 1)}`,
    v.n_conv != null ? `n = ${fmtInt(v.n_conv)} converters` : null,
    v.z != null ? `z = ${v.z.toFixed(2)}` : null,
    v.reach_pct != null ? `reach ${fmtPct(v.reach_pct, 1)} of ${baselineLabel} (incl. unknown)` : null,
    v.low_support ? 'too few converters for a reliable index' : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div
      className={`grid grid-cols-[minmax(6rem,10rem)_1fr_3.5rem_1.75rem] items-center gap-2 py-1 ${
        v.significant === false ? 'opacity-60' : ''
      }`}
      title={tooltip}
    >
      <div className="text-sm text-gray-800 truncate" title={v.value_label}>
        {v.value_label}
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Bar pct={v.share_conv} className={v.share_conv == null ? 'bg-transparent' : 'bg-[#0062FB]'} />
          <span className="w-12 text-right text-xs tabular-nums text-gray-800" title={v.share_conv == null ? 'no converter data' : undefined}>
            {fmtPct(v.share_conv)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Bar pct={v.share_base} className="bg-gray-400/70" />
          <span className="w-12 text-right text-xs tabular-nums text-gray-500">{fmtPct(v.share_base)}</span>
        </div>
      </div>
      <span
        className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums ${indexTone(
          v.index,
          v.significant
        )}`}
        aria-label={`index ${fmtIndex(v.index)}`}
      >
        {v.index == null ? 'n/a' : fmtIndex(v.index)}
        {v.significant ? <span className="ml-0.5" aria-label="significant">★</span> : null}
      </span>
      {onAddRule && v.index != null ? (
        (() => {
          const under = v.index < 100;
          const verb = under ? 'Exclude' : 'Include';
          return (
            <button
              type="button"
              onClick={() => onAddRule(v)}
              className={`p-1 rounded ${
                active
                  ? 'text-green-600'
                  : under
                    ? 'text-gray-400 hover:text-red-700 hover:bg-red-50'
                    : 'text-gray-400 hover:text-[#0062FB] hover:bg-blue-50'
              }`}
              aria-label={active ? `Rule added: ${verb.toLowerCase()} ${v.value_label}` : `${verb} ${v.value_label} as a rule`}
              title={
                active
                  ? `Added as rule (${under ? 'exclusion' : 'inclusion'})`
                  : under
                    ? `Exclude "${v.value_label}" — converters are less likely to have it`
                    : `Include "${v.value_label}" — converters are more likely to have it`
              }
            >
              {active ? <Check className="h-4 w-4" /> : under ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            </button>
          );
        })()
      ) : (
        <span />
      )}
    </div>
  );
}

export default function FeatureCard({ feature, baselineLabel, onAddRule, isRuleActive }) {
  const [showAll, setShowAll] = useState(false);
  const values = feature.values || [];
  const visible = showAll ? values : values.slice(0, COLLAPSED_ROWS);

  return (
    <section className="rounded-lg border bg-white p-4" aria-label={feature.display_label}>
      <header className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{feature.display_label}</h3>
          <p className="text-xs text-gray-500">
            coverage {fmtPct(feature.coverage_conv)} converters · {fmtPct(feature.coverage_base)} {baselineLabel}
            {feature.n_conv != null ? ` · n=${fmtInt(feature.n_conv)}` : ''}
            {feature.is_ef_derived ? ' · network activity' : ''}
          </p>
        </div>
        {feature.distinctiveness != null ? (
          <span
            className="shrink-0 text-xs text-gray-500 tabular-nums"
            title="Distinctiveness: share of converters that would have to change value to match the baseline"
          >
            Δ {feature.distinctiveness.toFixed(1)} pts
          </span>
        ) : null}
      </header>
      <div className="grid grid-cols-[minmax(6rem,10rem)_1fr_3.5rem_1.75rem] gap-2 text-[11px] uppercase tracking-wide text-gray-400 mb-1">
        <span>Value</span>
        <span className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><i className="inline-block h-2 w-3 rounded bg-[#0062FB]" /> converters</span>
          <span className="inline-flex items-center gap-1"><i className="inline-block h-2 w-3 rounded bg-gray-400/70" /> {baselineLabel}</span>
        </span>
        <span className="text-right">Index</span>
        <span title="+ include as rule (over-indexed) · − exclude as rule (under-indexed)">rule</span>
      </div>
      <div className="divide-y divide-gray-50">
        {visible.map((v) => (
          <ValueRow key={v.value} v={v} onAddRule={onAddRule ? (val) => onAddRule(feature, val) : null} isRuleActive={isRuleActive ? (val) => isRuleActive(feature, val) : null} baselineLabel={baselineLabel} />
        ))}
      </div>
      {values.length > COLLAPSED_ROWS ? (
        <button type="button" onClick={() => setShowAll((s) => !s)} className="mt-2 text-xs text-[#0062FB] hover:underline">
          {showAll ? 'Show fewer' : `Show all ${values.length} values`}
        </button>
      ) : null}
    </section>
  );
}
